import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ListingsProvider } from "@/components/ListingsProvider";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const ListingsPage = lazy(() => import("./pages/ListingsPage"));
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
const NeighborhoodLanding = lazy(() => import("./pages/NeighborhoodLanding"));
const SellProperty = lazy(() => import("./pages/SellProperty"));
const PropertyValuation = lazy(() => import("./pages/PropertyValuation"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));

// GEO Pages
const GeoKnowledgeHub = lazy(() => import("./pages/GeoKnowledgeHub"));
const CornerAnswersPage = lazy(() => import("./pages/CornerAnswersPage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const MarketReportPage = lazy(() => import("./pages/MarketReportPage"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const AdvisorProfile = lazy(() => import("./pages/AdvisorProfile"));

const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const LuxuryProjectsPage = lazy(() => import("./pages/LuxuryProjectsPage"));
const CommercialRealEstatePage = lazy(() => import("./pages/CommercialRealEstatePage"));
const LandListingsPage = lazy(() => import("./pages/LandListingsPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));

const RentalContract = lazy(() => import("./pages/RentalContract"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const TadilatDekorasyon = lazy(() => import("./pages/TadilatDekorasyon"));
const TadilatDetail = lazy(() => import("./pages/TadilatDetail"));

import { WhatsAppButton } from "@/components/WhatsAppButton";

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ListingsProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
            <Route path="/" element={<Index />} />
            
            {/* Real Estate Portfolios & Special Catalogs */}
            <Route path="/satilik" element={<ListingsPage forcedType="satilik" />} />
            <Route path="/satilik/:category" element={<ListingsPage forcedType="satilik" />} />
            <Route path="/kiralik" element={<ListingsPage forcedType="kiralik" />} />
            <Route path="/kiralik/:category" element={<ListingsPage forcedType="kiralik" />} />
            <Route path="/luks-projeler" element={<LuxuryProjectsPage />} />
            <Route path="/ticari-gayrimenkul" element={<CommercialRealEstatePage />} />
            <Route path="/arsa" element={<LandListingsPage />} />
            <Route path="/ilan/:slug" element={<ListingDetail />} />

            {/* Services */}
            <Route path="/hizmetler" element={<ServicesPage />} />
            <Route path="/hizmetler/:slug" element={<ServicesPage />} />

            {/* GEO Knowledge & AI Optimization Routes */}
            <Route path="/adana-gayrimenkul-rehberi" element={<GeoKnowledgeHub />} />
            <Route path="/corner-cevapliyor" element={<CornerAnswersPage />} />
            <Route path="/corner-cevapliyor/:slug" element={<CornerAnswersPage />} />
            <Route path="/karsilastirma" element={<ComparisonPage />} />
            <Route path="/karsilastirma/:slug" element={<ComparisonPage />} />
            <Route path="/raporlar/:slug" element={<MarketReportPage />} />

            {/* SEO Location Landing Pages */}
            <Route path="/adana" element={<NeighborhoodLanding />} />
            <Route path="/adana/:ilce" element={<NeighborhoodLanding />} />
            <Route path="/adana/:ilce/:mahalle" element={<NeighborhoodLanding />} />

            {/* Seller & Valuation Lead Pages */}
            <Route path="/gayrimenkulumu-sat" element={<SellProperty />} />
            <Route path="/degerleme" element={<PropertyValuation />} />

            {/* Blog & Knowledge Hub */}
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />

            {/* Brand & Advisor Profiles & Contact */}
            <Route path="/hakkimizda" element={<AboutUs />} />
            <Route path="/ekibimiz" element={<TeamPage />} />
            <Route path="/danisman/:slug" element={<AdvisorProfile />} />
            <Route path="/iletisim" element={<ContactPage />} />

            {/* Utility Pages */}
            <Route path="/kira-sozlesmesi" element={<RentalContract />} />
            <Route path="/tadilat-dekorasyon" element={<TadilatDekorasyon />} />
            <Route path="/tadilat-dekorasyon/:id" element={<TadilatDetail />} />
            
            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <WhatsAppButton />
        </ListingsProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;

